import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import { DateFilterProvider, useDateFilter } from '../contexts/DateFilterContext';
import AccountTransactionPanel from '../components/AccountTransactionPanel';

function AccountTransactionsPageContent() {
  const navigate = useNavigate();
  const { accountCode: encodedAccountCode = '' } = useParams();
  const accountCode = encodedAccountCode;
  const [searchParams] = useSearchParams();
  const { startDate: fiscalStartDate, endDate: fiscalEndDate } = useDateFilter();

  const returnTo = searchParams.get('return') || '/trial-balance';
  const returnLabel = searchParams.get('returnLabel') || tr('Trial Balance', '試算表', '试算表');

  // Keep the back destination internal. This prevents an external `return`
  // query parameter from turning the breadcrumb into a redirect.
  const startDate = searchParams.get('start_date') || fiscalStartDate;
  const endDate = searchParams.get('end_date') || fiscalEndDate;

  const safeReturnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/trial-balance';

  const handleClose = useCallback(() => navigate(safeReturnTo), [navigate, safeReturnTo]);

  const handlePostClick = (bankStatementId: string, transactionId: string) => {
    navigate(`/bank-statements?statement=${encodeURIComponent(bankStatementId)}&highlight=${encodeURIComponent(transactionId)}`);
  };

  return (
    <div className="mx-auto w-[95%] space-y-4 py-6">
      <button
        type="button"
        onClick={() => navigate(safeReturnTo)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {tr('Back to', '返回', '返回')} {returnLabel}
      </button>

      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {tr('Account details', '科目詳情', '科目详情')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {startDate} – {endDate}
        </p>
      </div>

      <div className="rounded-2xl border bg-card shadow-sm">
        <AccountTransactionPanel
          accountCode={accountCode}
          accountName=""
          startDate={startDate}
          endDate={endDate}
          onClose={handleClose}
          onPostClick={handlePostClick}
        />
      </div>
    </div>
  );
}

export default function AccountTransactionsPage() {
  return (
    <DateFilterProvider>
      <AccountTransactionsPageContent />
    </DateFilterProvider>
  );
}
